// =============================================================================
// SimSense Marketplace — Shop Profile Page
// =============================================================================
// OT-280: Creator/shop storefront with banner, stats, featured setups, catalog.

import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCreatorProfile } from "../../hooks/useMarketplace";
import { SetupCard } from "./components/SetupCard";
import "./ShopProfilePage.css";

export function ShopProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { profile, setups, loading } = useCreatorProfile(slug);
  const [activeTab, setActiveTab] = useState<"setups" | "bundles" | "about" | "reviews">("setups");

  if (loading) {
    return <div className="shop-loading">Loading shop...</div>;
  }

  if (!profile) {
    return (
      <div className="shop-not-found">
        <h2>Shop not found</h2>
        <button onClick={() => navigate("/marketplace")}>
          Back to Marketplace
        </button>
      </div>
    );
  }

  const handleInstall = (id: string) => {
    console.log(`Installing setup ${id}`);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/marketplace/setup/${id}`);
  };

  // Featured setups (pinned by shop owner)
  const featuredIds = new Set(profile.featuredProductIds || []);
  const featuredSetups = setups.filter(s => featuredIds.has(s.id));
  const isShop = profile.creatorType === "shop_owner";

  // Count unique cars
  const uniqueCars = new Set(setups.map(s => s.carName)).size;

  return (
    <div className="shop-profile-page">
      {/* Shop Banner */}
      <div className="shop-banner">
        {profile.shopBannerUrl ? (
          <img src={profile.shopBannerUrl} alt="" className="banner-image" />
        ) : (
          <div className="banner-overlay" />
        )}
      </div>

      <div className="shop-body">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <Link to="/marketplace">Marketplace</Link>
          <span className="bc-sep">/</span>
          <span className="bc-active">{profile.shopName}</span>
        </div>

        {/* Shop Identity Card */}
        <div className="shop-identity-card">
          <div className="shop-logo-container">
            {profile.shopLogoUrl ? (
              <img
                src={profile.shopLogoUrl}
                alt={profile.shopName}
                className="shop-logo"
              />
            ) : (
              <div className="shop-logo-placeholder">
                {profile.shopName.charAt(0)}
              </div>
            )}
          </div>
          <div className="shop-info">
            <h1>
              {profile.shopName}
              {profile.isVerified && (
                <span className="shop-verified">
                  {isShop ? "Verified Shop" : "Verified Creator"}
                </span>
              )}
            </h1>
            <p className="shop-description">{profile.shopDescription}</p>
            <div className="shop-stats-row">
              <span className="shop-stat">
                <strong>{profile.totalSetups}</strong> Setups
              </span>
              <span className="shop-stat">
                <strong>{profile.avgRating.toFixed(1)}</strong> Avg Rating
              </span>
              <span className="shop-stat">
                <strong>{profile.totalDownloads.toLocaleString()}</strong>{" "}
                Downloads
              </span>
              <span className="shop-stat">
                <strong>{uniqueCars}</strong> Cars
              </span>
            </div>
          </div>
        </div>

        {/* Featured This Week (shop owners only) */}
        {isShop && featuredSetups.length > 0 && (
          <section className="featured-section">
            <h2 className="featured-title">Featured This Week</h2>
            <div className="setup-grid">
              {featuredSetups.map((setup) => (
                <SetupCard
                  key={setup.id}
                  setup={setup}
                  onInstall={handleInstall}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          </section>
        )}

        {/* Tabs */}
        <div className="shop-tabs">
          <button
            className={`shop-tab ${activeTab === "setups" ? "active" : ""}`}
            onClick={() => setActiveTab("setups")}
          >
            All Setups ({setups.length})
          </button>
          <button
            className={`shop-tab ${activeTab === "bundles" ? "active" : ""}`}
            onClick={() => setActiveTab("bundles")}
          >
            Packs & Bundles
          </button>
          <button
            className={`shop-tab ${activeTab === "about" ? "active" : ""}`}
            onClick={() => setActiveTab("about")}
          >
            About
          </button>
          <button
            className={`shop-tab ${activeTab === "reviews" ? "active" : ""}`}
            onClick={() => setActiveTab("reviews")}
          >
            Reviews
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "setups" && (
          <div className="setup-grid">
            {setups.length > 0 ? (
              setups.map((setup) => (
                <SetupCard
                  key={setup.id}
                  setup={setup}
                  onInstall={handleInstall}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="empty-state">
                No setups published yet.
              </div>
            )}
          </div>
        )}

        {activeTab === "bundles" && (
          <div className="empty-state">
            Packs & Bundles coming soon. Shops will be able to offer subscription
            access to their full catalog or themed bundles.
          </div>
        )}

        {activeTab === "about" && (
          <div className="about-section">
            <div className="about-card">
              <h3>About {profile.shopName}</h3>
              <p>{profile.shopDescription}</p>
              {profile.iracingIrating && (
                <div className="about-credentials">
                  <div className="credential">
                    <span className="credential-label">iRating</span>
                    <span className="credential-value">{profile.iracingIrating.toLocaleString()}</span>
                  </div>
                  {profile.iracingLicense && (
                    <div className="credential">
                      <span className="credential-label">License</span>
                      <span className="credential-value">{profile.iracingLicense}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="empty-state">
            Shop reviews coming soon. Reviews from individual setups contribute
            to the shop's overall rating.
          </div>
        )}
      </div>
    </div>
  );
}
